import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy"
        description="Learn how Sksenq LLC collects, uses, and protects your personal data. We only collect information necessary to respond to your inquiries."
        canonical="/privacy-policy"
      />
      <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8 gap-2">
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-lg dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: 28 December 2025</p>

          <p>
            This Privacy Policy explains how Sksenq LLC ("we", "us", or "our") collects, uses, and protects personal data when you interact with our website https://sksenq.com.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">1. Who We Are</h2>
          <ul className="list-none pl-0 space-y-1">
            <li><strong>Legal entity:</strong> Sksenq LLC</li>
            <li><strong>Country of registration:</strong> Armenia</li>
            <li><strong>Website:</strong> <a href="https://sksenq.com" className="text-primary hover:underline">https://sksenq.com</a></li>
            <li><strong>Contact email:</strong> <a href="mailto:contact@sksenq.com" className="text-primary hover:underline">contact@sksenq.com</a></li>
            <li><strong>Privacy email:</strong> <a href="mailto:privacy@sksenq.com" className="text-primary hover:underline">privacy@sksenq.com</a></li>
          </ul>
          <p>Sksenq LLC provides software engineering and cloud consulting services worldwide.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">2. What Personal Data We Collect</h2>
          <p>
            When you submit a request through our contact form, we collect only the information necessary to respond to your inquiry and verify its authenticity.
          </p>
          <p>The contact form includes the following fields:</p>
          <ul>
            <li>Name (required)</li>
            <li>Company name (optional)</li>
            <li>Work email address (required)</li>
            <li>Message content (required)</li>
          </ul>
          <p>We do not collect:</p>
          <ul>
            <li>Payment or billing information</li>
            <li>User account credentials</li>
            <li>Marketing or newsletter subscriptions</li>
            <li>Tracking or analytics data</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">3. Work Email Verification</h2>
          <p>After submitting the contact form, we send a verification code to the provided work email address to confirm that:</p>
          <ul>
            <li>The email address is valid and reachable</li>
            <li>The request was submitted by a real person</li>
            <li>Automated or spam submissions are prevented</li>
          </ul>
          <p>
            Verification codes are used only for verification purposes, are time-limited, and are not retained longer than necessary after verification is completed.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">4. How We Use Your Data</h2>
          <p>We use the collected personal data strictly to:</p>
          <ul>
            <li>Verify the authenticity of your request</li>
            <li>Respond to your inquiry</li>
            <li>Communicate regarding potential engineering or consulting services</li>
          </ul>
          <p>
            We do not use your data for marketing, advertising, automated decision-making, profiling, or sale to third parties.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">5. Legal Basis for Processing</h2>
          <p>
            If you are located in the European Union or other jurisdictions with similar regulations, we process your personal data based on:
          </p>
          <ul>
            <li>Your consent when submitting and verifying your request</li>
            <li>Our legitimate interest in preventing spam and responding to business inquiries</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">6. Data Storage and Processing</h2>
          <p>Contact requests and verification status are stored securely using AWS services.</p>
          <p>
            Email communication is handled through internal or infrastructure service providers strictly for operational purposes.
          </p>
          <p>
            Appropriate technical and organizational measures are applied to protect personal data from unauthorized access, loss, or misuse.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">7. Data Sharing</h2>
          <p>We do not sell, rent, or trade personal data.</p>
          <p>
            Your data may be disclosed only when required by law or when necessary to operate the contact and verification process using trusted technical service providers.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">8. International Data Transfers</h2>
          <p>
            As Sksenq LLC operates internationally, your data may be processed in Armenia or other countries where our infrastructure or service providers are located.
          </p>
          <p>Where applicable, appropriate safeguards are applied in accordance with data protection laws.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">9. Data Retention</h2>
          <p>
            We retain personal data only for as long as necessary to verify and process your inquiry and communicate regarding your request.
          </p>
          <p>You may request deletion of your personal data at any time.</p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">10. Your Rights</h2>
          <p>
            Depending on your location, you may have the right to access, correct, delete, or object to the processing of your personal data, as well as withdraw consent.
          </p>
          <p>
            Requests can be sent to: <a href="mailto:privacy@sksenq.com" className="text-primary hover:underline">privacy@sksenq.com</a>
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">11. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under the age of 16. We do not knowingly collect personal data from children.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Any changes will be published on this page with an updated date.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">13. Contact Information</h2>
          <p>For privacy-related questions or requests, please contact:</p>
          <ul className="list-none pl-0">
            <li><a href="mailto:privacy@sksenq.com" className="text-primary hover:underline">privacy@sksenq.com</a></li>
            <li><a href="mailto:contact@sksenq.com" className="text-primary hover:underline">contact@sksenq.com</a></li>
          </ul>
        </article>
      </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
